import apiClient from './api.service';
import {
  ComplementaryAnalysisResponse,
  ComplementaryPublishResponse,
  ComplementaryUploadTargetResponse,
  ComplementaryUploadResponse,
} from '../types/analysis-complementary.types';
import { SurveyVisibilityDeniedResponse } from '../types/survey-visibility.types';

// ============================================================================
// Types
// ============================================================================

export type SurveyAnalysisStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED_UNPUBLISHED'
  | 'PUBLISHED';

export interface ChoiceOption {
  label: string;
  count: number;
  percent: number;
}

export interface ChoiceSummary {
  options: ChoiceOption[];
}

export interface RatingSummary {
  average: number;
}

export interface BooleanSummary {
  yes: { count: number; percent: number };
  no: { count: number; percent: number };
}

export type QuestionSummary = ChoiceSummary | RatingSummary | BooleanSummary;

export interface ChoiceSegmentOption {
  option: string;
  count: number;
  percent: number;
}

export type ProfileField =
  | 'gender'
  | 'age_group'
  | 'education'
  | 'economic_level'
  | 'state'
  | 'city';

export interface QuestionAnalyticsSegment {
  /** For CHOICE/CHOICES: array of { option, count, percent } per group */
  [groupLabel: string]: ChoiceSegmentOption[] | number | BooleanSummary;
}

export interface QuestionAnalytics {
  gender?: QuestionAnalyticsSegment;
  age_group?: QuestionAnalyticsSegment;
  education?: QuestionAnalyticsSegment;
  economic_level?: QuestionAnalyticsSegment;
  state?: QuestionAnalyticsSegment;
  city?: QuestionAnalyticsSegment;
}

export interface GroupInfo {
  id: string;
  name: string;
  scope: 'NATIONAL' | 'STATE';
  state_name: string | null;
  state_code: string | null;
  order: number;
}

export interface QuestionAnalysisResult {
  question_id: string;
  short_description: string;
  answer_type: 'CHOICE' | 'CHOICES' | 'NUMBER' | 'BOOLEAN';
  question_type: string;
  total_valid: number;
  summary: QuestionSummary;
  analytics: QuestionAnalytics;
  group?: GroupInfo | null;
}

export interface SurveyAnalysisPayload {
  survey_id: string;
  generated_at: string;
  total_respondents: number;
  questions: QuestionAnalysisResult[];
  groups?: GroupInfo[] | null;
}

export interface SurveyAnalysisResponse {
  surveyId: number;
  status: SurveyAnalysisStatus;
  generatedAt: string | null;
  publishedAt: string | null;
  publishedBy: number | null;
  versionId?: string | null;
  analyticsMode?: 'QUERY_API' | 'LEGACY_PAYLOAD';
  payload: SurveyAnalysisPayload | null;
}

export interface AnalysisPendingResponse {
  status: 'PENDING' | 'IN_PROGRESS';
  message: string;
}

export interface PublishAnalysisResponse {
  surveyId: number;
  status: 'PUBLISHED';
  publishedAt: string;
  publishedBy: number;
}

export function parseVisibilityDeniedError(error: any): SurveyVisibilityDeniedResponse | null {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return null;
  }

  if (typeof data.code === 'string' && typeof data.message === 'string' && data.visibility) {
    return data as SurveyVisibilityDeniedResponse;
  }

  return null;
}

// ============================================================================
// Service
// ============================================================================

export const surveyAnalysisService = {
  async legacyUploadComplementaryAttachment(
    surveyId: number,
    file: File
  ): Promise<ComplementaryUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.put(
      `/api/surveys/${surveyId}/analysis/complementary/attachment`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * GET /api/surveys/:surveyId/analysis
   * Returns 200 with full payload or 202 if still processing.
   */
  async getAnalysis(
    surveyId: number
  ): Promise<SurveyAnalysisResponse | AnalysisPendingResponse> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analysis`);
    return response.data;
  },

  /**
   * POST /api/surveys/:surveyId/analysis/publish
   * Publishes the analysis. Irreversible.
   */
  async publishAnalysis(surveyId: number): Promise<PublishAnalysisResponse> {
    const response = await apiClient.post(
      `/api/surveys/${surveyId}/analysis/publish`
    );
    return response.data;
  },

  async getComplementaryAnalysis(
    surveyId: number
  ): Promise<ComplementaryAnalysisResponse> {
    const response = await apiClient.get(
      `/api/surveys/${surveyId}/analysis/complementary`
    );
    return response.data;
  },

  async updateComplementaryContent(
    surveyId: number,
    content: string
  ): Promise<ComplementaryAnalysisResponse> {
    const response = await apiClient.patch(
      `/api/surveys/${surveyId}/analysis/complementary`,
      { content }
    );
    return response.data;
  },

  async uploadComplementaryAttachment(
    surveyId: number,
    file: File
  ): Promise<ComplementaryUploadResponse> {
    try {
      const targetResponse = await apiClient.post<ComplementaryUploadTargetResponse>(
        `/api/surveys/${surveyId}/analysis/complementary/attachment/upload-url`,
        {
          fileName: file.name,
          mimeType: file.type || 'application/pdf',
          sizeBytes: file.size,
        }
      );

      const target = targetResponse.data;

      if (
        target.mode !== 'DIRECT_PRESIGNED' ||
        !target.uploadUrl ||
        !target.storageKey
      ) {
        return this.legacyUploadComplementaryAttachment(surveyId, file);
      }

      const uploadResponse = await fetch(target.uploadUrl, {
        method: target.method || 'PUT',
        headers: {
          'Content-Type': file.type || 'application/pdf',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Falha ao enviar arquivo para storage');
      }

      const completeResponse = await apiClient.post<ComplementaryUploadResponse>(
        `/api/surveys/${surveyId}/analysis/complementary/attachment/complete`,
        {
          storageKey: target.storageKey,
          fileName: file.name,
          mimeType: file.type || 'application/pdf',
          sizeBytes: file.size,
        }
      );

      return completeResponse.data;
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        return this.legacyUploadComplementaryAttachment(surveyId, file);
      }

      throw error;
    }
  },

  async removeComplementaryAttachment(surveyId: number): Promise<void> {
    await apiClient.delete(`/api/surveys/${surveyId}/analysis/complementary/attachment`);
  },

  async publishComplementaryAnalysis(
    surveyId: number
  ): Promise<ComplementaryPublishResponse> {
    const response = await apiClient.post(
      `/api/surveys/${surveyId}/analysis/complementary/publish`
    );
    return response.data;
  },
};

export default surveyAnalysisService;
